// src/pages/Admin/ManageUsers/ManageUsers.jsx
import React, { useEffect, useState, useRef } from "react";
import { db } from "../../../config/firebase";
import { ref, update, onValue, off } from "firebase/database";
import { getAuth } from "firebase/auth";
import { NavBarLogin } from "../../../components";
import { useNavigate } from "react-router-dom";
import "./manageUsers.scss";

const DELETE_USER_URL = "https://deleteuseraccount-h3wvle3q2a-uc.a.run.app";
const CHANGE_PASSWORD_URL =
  "https://changeuserpassword-h3wvle3q2a-uc.a.run.app";

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [toast, setToast] = useState(null);
  const [passwordChange, setPasswordChange] = useState({});
  const previousUsers = useRef({});
  const navigate = useNavigate();
  const auth = getAuth();

  // 🔹 Listener realtime untuk users
  useEffect(() => {
    const usersRef = ref(db, "users");
    setLoading(true);

    const unsubscribe = onValue(
      usersRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const usersList = Object.keys(data).map((uid) => ({
            uid,
            ...data[uid],
          }));

          // Deteksi perubahan (user baru, verifikasi, login)
          detectRealtimeChanges(previousUsers.current, data);
          previousUsers.current = data;

          setUsers(usersList);
        } else {
          setUsers([]);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Realtime error:", error);
        setMessage({
          type: "error",
          text: "Gagal memuat data pengguna secara realtime.",
        });
        setLoading(false);
      }
    );

    return () => off(usersRef);
  }, []);

  // 🔸 Deteksi perubahan data realtime
  const detectRealtimeChanges = (oldData, newData) => {
    // User baru
    const newUids = Object.keys(newData);
    const oldUids = Object.keys(oldData || {});
    const addedUser = newUids.find((uid) => !oldUids.includes(uid));

    if (addedUser) {
      showToast(`Akun baru terdaftar: ${newData[addedUser].email}`);
      return;
    }

    // Perubahan status verifikasi
    for (let uid of newUids) {
      if (oldData[uid] && oldData[uid].verified !== newData[uid].verified) {
        const status = newData[uid].verified ? "terverifikasi" : "dibatalkan";
        showToast(
          `Status verifikasi ${newData[uid].email} telah ${status}.`
        );
        return;
      }
    }

    // Update login terakhir
    for (let uid of newUids) {
      if (oldData[uid] && oldData[uid].lastLogin !== newData[uid].lastLogin) {
        showToast(`Login terakhir diperbarui oleh ${newData[uid].email}`);
        return;
      }
    }
  };

  // 🔹 Fungsi tampilkan notifikasi kecil
  const showToast = (text) => {
    setToast(text);
    setTimeout(() => setToast(null), 4000); // hilang otomatis
  };

  const handleVerifyToggle = async (uid, currentStatus) => {
    try {
      const userRef = ref(db, `users/${uid}`);
      await update(userRef, { verified: !currentStatus });
      setMessage({
        type: "success",
        text: `Status verifikasi berhasil diubah.`,
      });
    } catch (error) {
      console.error("Gagal update status:", error);
      setMessage({
        type: "error",
        text: "Gagal memperbarui status verifikasi.",
      });
    }
  };

  const handleDelete = async (uid) => {
    if (!window.confirm("Yakin ingin menghapus akun ini?")) return;

    try {
      const response = await fetch(DELETE_USER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid }),
      });
      const data = await response.json();

      if (data.success) {
        alert("Akun berhasil dihapus.");
      } else {
        alert("Gagal menghapus akun: " + data.message);
      }
    } catch (error) {
      console.error("Error saat fetch delete:", error);
      alert("Terjadi kesalahan jaringan atau server.");
    }
  };

  const handleChangePassword = async (uid) => {
    const newPassword = passwordChange[uid];
    if (!newPassword || newPassword.length < 6) {
      alert("Password minimal 6 karakter.");
      return;
    }

    try {
      const response = await fetch(CHANGE_PASSWORD_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, newPassword }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({
          type: "success",
          text: `Password berhasil diganti.`,
        });
        setPasswordChange((prev) => ({ ...prev, [uid]: "" }));
      } else {
        throw new Error(data.message || "Gagal mengganti password.");
      }
    } catch (error) {
      console.error("Gagal mengganti password:", error);
      setMessage({
        type: "error",
        text: `Gagal mengganti password: ${error.message}`,
      });
    }
  };

  const getIndonesianTimeZoneLabel = () => {
    const offset = new Date().getTimezoneOffset() / -60;
    switch (offset) {
      case 7:
        return "WIB";
      case 8:
        return "WITA";
      case 9:
        return "WIT";
      default:
        return "Zona Lokal";
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const labelZona = getIndonesianTimeZoneLabel();

    const tanggal = date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone,
    });
    const waktu = date.toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone,
    });

    return `${tanggal}, ${waktu} ${labelZona}`;
  };

  const formatDateOnly = (timestamp) => {
    if (!timestamp) return "-";
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return new Date(timestamp).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone,
    });
  };

  return (
    <>
      <NavBarLogin />
      <div className="manage-users-container">
        <div className="header-section">
          <h2>Manajemen Akun Pengguna</h2>
          <button className="back-button" onClick={() => navigate("/admin")}>
            ← Kembali ke Dashboard
          </button>
        </div>

        <p>
          Total akun terdaftar: <strong>{users.length}</strong>
        </p>

        {message && (
          <div
            className={`message ${
              message.type === "success" ? "success" : "error"
            }`}
          >
            {message.text}
          </div>
        )}

        {toast && (
          <div className="toast-notif">
            <span>{toast}</span>
          </div>
        )}

        {loading ? (
          <p>Sedang memuat data...</p>
        ) : users.length === 0 ? (
          <p>Tidak ada pengguna terdaftar.</p>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>No</th>
                <th>Nama Lengkap</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status Verifikasi</th>
                <th>Login Terakhir</th>
                <th>Tanggal Daftar</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.uid}>
                  <td>{index + 1}</td>
                  <td>
                    {user.firstName} {user.lastName}
                  </td>
                  <td>{user.email}</td>
                  <td>{user.role}</td>
                  <td className={user.verified ? "verified" : "unverified"}>
                    {user.verified ? "Terverifikasi" : "Menunggu"}
                  </td>
                  <td>{formatDateTime(user.lastLogin)}</td>
                  <td>{formatDateOnly(user.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() =>
                          handleVerifyToggle(user.uid, user.verified)
                        }
                        className={`verify-btn ${
                          user.verified ? "cancel" : "verify"
                        }`}
                      >
                        {user.verified ? "Batalkan" : "Verifikasi"}
                      </button>

                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(user.uid)}
                      >
                        Hapus
                      </button>
                    </div>

                    <div className="password-section">
                      <input
                        type="password"
                        placeholder="Password baru"
                        value={passwordChange[user.uid] || ""}
                        onChange={(e) =>
                          setPasswordChange({
                            ...passwordChange,
                            [user.uid]: e.target.value,
                          })
                        }
                      />
                      <button
                        className="change-pass-btn"
                        onClick={() => handleChangePassword(user.uid)}
                      >
                        Ganti
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 🔹 Style notifikasi kecil (inline agar langsung siap pakai) */}
      <style>{`
        .toast-notif {
          position: fixed;
          bottom: 25px;
          right: 25px;
          background: #333;
          color: #fff;
          padding: 10px 16px;
          border-radius: 8px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.2);
          opacity: 0.95;
          animation: fadeInOut 4s ease forwards;
          z-index: 1000;
          font-size: 14px;
        }

        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(20px); }
          10% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; }
          100% { opacity: 0; transform: translateY(20px); }
        }
      `}</style>
    </>
  );
};

export default ManageUsers;
